import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  Mail,
  Eye,
  Clock,
} from "lucide-react";

export const DashboardHome = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="admin-dashboard-home">
      <h1 className="font-heading text-3xl text-charcoal mb-8">Dashboard Overview</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        <div className="glass rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <Eye className="w-8 h-8 text-jade-text" />
            <span className="text-xs font-body text-taupe uppercase">All Time</span>
          </div>
          <p className="font-heading text-3xl text-charcoal">{stats?.total_pageviews || 0}</p>
          <p className="font-body text-sm text-stone-grey">Total Page Views</p>
        </div>

        <div className="glass rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <Mail className="w-8 h-8 text-champagne-gold" />
            <span className="text-xs font-body text-taupe uppercase">Pending: {stats?.pending_inquiries || 0}</span>
          </div>
          <p className="font-heading text-3xl text-charcoal">{stats?.total_inquiries || 0}</p>
          <p className="font-body text-sm text-stone-grey">Total Inquiries</p>
        </div>

        <div className="glass rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <Users className="w-8 h-8 text-blush-pink" />
          </div>
          <p className="font-heading text-3xl text-charcoal">{stats?.total_subscribers || 0}</p>
          <p className="font-body text-sm text-stone-grey">Newsletter Subscribers</p>
        </div>

        <div className="glass rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <Clock className="w-8 h-8 text-jade-soft" />
            <span className="text-xs font-body text-taupe uppercase">Last 7 Days</span>
          </div>
          <p className="font-heading text-3xl text-charcoal">{stats?.recent_pageviews_7d || 0}</p>
          <p className="font-body text-sm text-stone-grey">Recent Page Views</p>
        </div>
      </div>

      {stats?.top_pages && stats.top_pages.length > 0 && (
        <div className="glass rounded-xl p-6">
          <h2 className="font-heading text-xl text-charcoal mb-6">Top Pages</h2>
          <div className="space-y-3">
            {stats.top_pages.map((page, index) => (
              <div key={page.page || index} className="flex items-center justify-between py-2 border-b border-sand last:border-0">
                <span className="font-body text-stone-grey">{page.page || "Home"}</span>
                <span className="font-body text-sm text-taupe">{page.count} views</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

